import React, { createContext, useContext, useEffect, useState, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useSensitiveFlow } from '../../../context/PwaUpdateContext';
import { createTestSessionStore, sessionKey } from '../engine/testSessionStore';
import { getTemplate } from '../templates/testTemplates';

const Context = createContext(null);
export const useTestSession = () => useContext(Context);
const emptySubscribe = () => () => {};
const emptySnapshot = () => null;
const TEST_PATH = '/development/tests';

function SessionRuntime({ userId, teamId, teamName, children }) {
  const location = useLocation(), navigate = useNavigate();
  const [store, setStore] = useState(null), [lockState, setLockState] = useState('idle');
  const [retry, setRetry] = useState(0);
  const [storage] = useState(() => {
    // Without Web Locks, keep drafts in this browser tab; never race shared writes.
    try { return navigator.locks?.request ? window.localStorage : window.sessionStorage; }
    catch { return { getItem() { throw new Error('Enhetens lagring är inte tillgänglig.'); }, setItem() { throw new Error(); }, removeItem() {} }; }
  });
  const key = sessionKey(userId, teamId);
  const [hasSaved, setHasSaved] = useState(() => { try { return !!storage.getItem(key); } catch { return false; } });
  const view = useSyncExternalStore(store?.subscribe || emptySubscribe, store?.getSnapshot || emptySnapshot, emptySnapshot);
  const hasSession = !!view && view.draft.step !== 'choose';
  const needsOwner = hasSession || hasSaved || location.pathname === TEST_PATH;
  useSensitiveFlow(hasSession && ['run', 'review'].includes(view.draft.step));

  useEffect(() => {
    if (!needsOwner) return undefined;
    let disposed = false, release, ownedStore;
    setLockState('loading');
    const own = () => {
      if (disposed) return;
      ownedStore = createTestSessionStore({ userId, teamId, storage });
      setStore(ownedStore); setHasSaved(false); setLockState('ready');
    };
    // Defer acquisition one microtask: React StrictMode's setup/cleanup probe
    // must not compete with the real mount for the same browser lock.
    Promise.resolve().then(async () => {
      if (disposed) return;
      if (navigator.locks?.request) {
        await navigator.locks.request(key, { ifAvailable: true }, async lock => {
          if (disposed) return;
          if (!lock) { setLockState('busy'); return; }
          own();
          await new Promise(resolve => { release = resolve; });
        });
      } else own();
    }).catch(() => { if (!disposed) setLockState('error'); });
    return () => {
      disposed = true; ownedStore?.destroy(); release?.(); setStore(null);
    };
  }, [needsOwner, key, storage, userId, teamId, retry]);

  useEffect(() => {
    if (!store) return undefined;
    const hidden = () => { if (document.hidden) store.checkpoint(); };
    const pagehide = () => store.leaveDocument();
    document.addEventListener('visibilitychange', hidden);
    window.addEventListener('pagehide', pagehide);
    return () => { document.removeEventListener('visibilitychange', hidden); window.removeEventListener('pagehide', pagehide); };
  }, [store]);

  const value = { store, view, lockState, retry: () => setRetry(v => v + 1) };
  return <Context.Provider value={value}>
    {children}
    {hasSession && location.pathname !== TEST_PATH && <div
      className="fixed z-40 left-4 right-4 bottom-20 md:left-auto md:right-6 md:bottom-6 md:max-w-sm rounded-xl border border-primary/40 bg-background shadow-lg p-3"
      data-testid="active-test-banner">
      <p className="text-sm font-semibold">Pågående test · {teamName}</p>
      <p className="text-xs text-muted-foreground mt-1">{getTemplate(view.draft.templateId)?.name}{view.audioStatus === 'paused' || view.audioStatus === 'error' ? ' · väntar på dig' : ''}</p>
      <button type="button" className="mt-2 text-sm font-semibold text-primary min-h-10" onClick={() => navigate(TEST_PATH)}>Återgå till testet →</button>
    </div>}
  </Context.Provider>;
}

export function TestSessionProvider({ children }) {
  const { user, activeTeam, isCoach, loading, teamSwitching } = useAuth();
  // Do not expose a previous account/team's draft while context is changing.
  if (loading || teamSwitching || !user?.id || !activeTeam?.id || !isCoach)
    return <Context.Provider value={null}>{children}</Context.Provider>;
  return <SessionRuntime key={`${user.id}:${activeTeam.id}`} userId={String(user.id)} teamId={String(activeTeam.id)} teamName={activeTeam.name}>
    {children}
  </SessionRuntime>;
}
